import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from '@mui/material';
import { useUserInfo } from '../../../../context/user';
import AddBalanceModal from './components/AddBalanceModal';
import { useAppServices } from '../../../../hook/services';
import { getCurrencySymbol } from '../../../../utils/currency';

export default function Wallet() {
  const [autoChargeLoading, setAutoChargeLoading] = useState(false);
  const [lowBalanceLoading, setLowBalanceLoading] = useState(false);
  const [period, setPeriod] = useState('all');
  const userActiveLocation = useUserInfo()[4]
  const saveActiveLocation = useUserInfo()[5]
  const [openAddBalance, setOpenAddBalance] = useState(false)
  const service = useAppServices()
  const [logs, setlogs] = useState([])
  const [credits, setcredits] = useState([])

  const filterLogsByDate = (logs) => {
    if (period === "all") return logs;

    const today = new Date();
    const rangeInDays = parseInt(period, 10);
    const startDate = new Date(today.setDate(today.getDate() - rangeInDays));

    return logs.filter((log) => {
      const logDate = new Date(log.createdAt);
      return logDate >= startDate;
    });
  };


  const fetchTransactionHistory = async () => {
    const { response } = await service.agency.GetChargelogs({
      query: `account_id=${userActiveLocation?._id}&accountId=${userActiveLocation?.id}&level=account`,
      toaster: true
    })
    const accountResp = await service.accounts.getbyid({ query: `id=${userActiveLocation?.id}` })
    if (response?.data) {
      setlogs(response.data)
      setcredits(response.credits)
    }
    if (accountResp?.response?.success && userActiveLocation?.Balance && userActiveLocation?.Balance > accountResp?.response?.data?.Balance) {
      saveActiveLocation(accountResp?.response?.data)
    }
  }

  useEffect(() => {
    if (userActiveLocation?._id)
      fetchTransactionHistory()
  }, [userActiveLocation])

  const handleAutoCharge = async (e) => {
    setAutoChargeLoading(true)
    const payload = {
      auto_charge: parseInt(e.target.value),
      _id: userActiveLocation?.id
    }
    const { response } = await service.accounts.update({ payload })
    if (response?.success)
      saveActiveLocation(response?.data)
    setAutoChargeLoading(false)
  }

  const handleLowBalance = async (e) => {
    setLowBalanceLoading(true)
    const payload = {
      low_balancs_limit: parseInt(e.target.value),
      _id: userActiveLocation?.id
    }
    const { response } = await service.accounts.update({ payload })
    if (response?.success)
      saveActiveLocation(response?.data)
    setLowBalanceLoading(false)
  }

  // All of this account's charges/credits share the SaaS plan's currency,
  // so derive one wallet-level currency from the logs (falls back to "$").
  const walletCurrency =
    logs.find((l) => l?.currency)?.currency ||
    credits.find((c) => c?.currency)?.currency ||
    null;
  const walletSymbol = getCurrencySymbol(walletCurrency);

  return (
    <Box sx={{ p: 3 }}>
      {/* Balance & Auto Recharge Card */}
      <Card sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" color="textSecondary">
              Your Balance
            </Typography>
            <Typography variant="h4" gutterBottom>
              {walletSymbol}{userActiveLocation?.Balance?.toFixed(2) || 0}
            </Typography>
            <Button variant="outlined" className='!text-black !border !border-gray-500 !normal-case !text-lg' onClick={() => setOpenAddBalance(true)}>Add Balance</Button>
          </Grid>
          <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          </Grid>
          <Grid item xs={12} md={7}>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              <Typography sx={{ mr: 1 }}>Auto recharge with:</Typography>
              {autoChargeLoading ? <CircularProgress size="30px" /> : <FormControl variant="outlined" size="small" sx={{ mr: 2, minWidth: 100 }}>
                <InputLabel>{walletSymbol}</InputLabel>
                <Select
                  label={walletSymbol}
                  value={userActiveLocation?.auto_charge || 0}
                  onChange={(e) => handleAutoCharge(e)}
                >
                  {[10, 25, 50, 100].map((amt) => (
                    <MenuItem key={amt} value={amt}>{walletSymbol}{amt}</MenuItem>
                  ))}
                </Select>
              </FormControl>}
              <Typography sx={{ mx: 1 }}>When balance is below</Typography>
              {lowBalanceLoading ? <CircularProgress size="30px" /> : <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{walletSymbol}</InputLabel>
                <Select
                  label={walletSymbol}
                  value={userActiveLocation?.low_balancs_limit || 0}
                  onChange={(e) => handleLowBalance(e)}
                >
                  {[10, 25, 50, 100].map((amt) => (
                    <MenuItem key={amt} value={amt}>{walletSymbol}{amt}</MenuItem>
                  ))}
                </Select>
              </FormControl>}
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Transactions Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box></Box>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Last</InputLabel>
          <Select label="Last" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box variant='div' display={'flex'} gap={'1rem'}>
        <Box className='w-full' variant='div'>
          <Typography variant="h6">Transactions</Typography>
          <List>
            {filterLogsByDate(logs)?.map((tx) => (
              <ListItem key={tx.id} divider>
                <ListItemText
                  primary={<Typography variant="subtitle2" fontWeight="bold" textTransform={'capitalize'}>{tx.req_type}</Typography>}
                  secondary={
                    <>
                      <Typography variant="body2" color="textSecondary">{userActiveLocation?.business_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{new Date(tx.createdAt).toLocaleString()}</Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Typography variant="h6" color={tx.type === 'debit' ? 'error' : 'success.main'}>
                    {tx.type === 'debit' ? '-' : '+'}{getCurrencySymbol(tx.currency || walletCurrency)}{tx.amount}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Transactions List */}
        <Box className='w-full'>
          <Typography variant="h6">Credits</Typography>
          <List>
            {filterLogsByDate(credits)?.map((tx) => (
              <ListItem key={tx.id} divider>
                <ListItemText
                  primary={<Typography variant="subtitle2" fontWeight="bold" textTransform={'capitalize'}>{tx.req_type}</Typography>}
                  secondary={
                    <>
                      <Typography variant="body2" color="textSecondary">{userActiveLocation?.business_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{new Date(tx.createdAt).toLocaleString()}</Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Typography variant="h6" color={tx.type === 'debit' ? 'error' : 'success.main'}>
                    {tx.type === 'debit' ? '-' : '+'}{getCurrencySymbol(tx.currency || walletCurrency)}{tx.amount}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
      {openAddBalance && <AddBalanceModal isOpen={openAddBalance} saveActiveLocation={saveActiveLocation} onClose={() => setOpenAddBalance(false)} userActiveLocation={userActiveLocation} service={service} />}
    </Box>
  );
}